import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../config/axios';

interface Medicine {
  medicine_id: number;
  medicine_name: string;
  category_id: number;
  category_name: string;
  expiry_date: string;
  unit_price: number;
  quantity: number;
  description: string;
  created_at: string;
  conversion_factor?: number;
  hike_percent?: number;
  is_active?: boolean;
  deleted_at?: string;
}

interface Category {
  category_id: number;
  category_name: string;
  description?: string;
}

interface MedicinesState {
  medicines: Medicine[];
  deletedMedicines: Medicine[];
  categories: Category[];
  loading: boolean;
  error: string | null;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

const initialState: MedicinesState = {
  medicines: [],
  deletedMedicines: [],
  categories: [],
  loading: false,
  error: null,
  pagination: {
    current: 1,
    pageSize: 5,
    total: 0,
    totalPages: 0,
  },
};

export const fetchMedicines = createAsyncThunk(
  'medicines/fetchMedicines',
  async (params: { page?: number; limit?: number; search?: string; category_id?: string } = {}) => {
    const { page = 1, limit = 5, search = '', category_id = '' } = params;
    const response = await apiClient.get('/medicines', {
      params: { page, limit, search, category_id }
    });
    return response.data;
  }
);

export const fetchCategories = createAsyncThunk(
  'medicines/fetchCategories',
  async () => {
    try {
      console.log('Fetching categories from /categories endpoint...');
      const response = await apiClient.get('/categories');
      console.log('Categories response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }
);

export const createMedicine = createAsyncThunk(
  'medicines/createMedicine',
  async (medicineData: Omit<Medicine, 'medicine_id' | 'created_at' | 'category_name'>) => {
    const response = await apiClient.post('/medicines', medicineData);
    return response.data;
  }
);

export const updateMedicine = createAsyncThunk(
  'medicines/updateMedicine',
  async ({ id, medicineData }: { id: number; medicineData: Omit<Medicine, 'medicine_id' | 'created_at' | 'category_name'> }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/medicines/${id}`, medicineData);
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update medicine';
      return rejectWithValue({ message: errorMessage });
    }
  }
);

export const deleteMedicine = createAsyncThunk(
  'medicines/deleteMedicine',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(`/medicines/${id}`);
      return { id, message: response.data.message || 'Medicine deleted successfully' };
    } catch (error: any) {
      // Extract error message from the response
      const errorMessage = error.response?.data?.error || error.message || 'Failed to delete medicine';
      return rejectWithValue({ message: errorMessage });
    }
  }
);

export const createCategory = createAsyncThunk(
  'medicines/createCategory',
  async (categoryData: Omit<Category, 'category_id'>) => {
    const response = await apiClient.post('/categories', categoryData);
    return response.data.data;
  }
);

export const updateCategory = createAsyncThunk(
  'medicines/updateCategory',
  async ({ id, categoryData }: { id: number; categoryData: Omit<Category, 'category_id'> }) => {
    const response = await apiClient.put(`/categories/${id}`, categoryData);
    return response.data.data;
  }
);

export const deleteCategory = createAsyncThunk(
  'medicines/deleteCategory',
  async (id: number) => {
    const response = await apiClient.delete(`/categories/${id}`);
    return { id, message: response.data.message };
  }
);

export const fetchDeletedMedicines = createAsyncThunk(
  'medicines/fetchDeletedMedicines',
  async () => {
    const response = await apiClient.get('/medicines', {
      params: { is_active: false }
    });
    return response.data;
  }
);

export const restoreMedicine = createAsyncThunk(
  'medicines/restoreMedicine',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(`/medicines/${id}/activate`);
      return { id, message: response.data.message || 'Medicine restored successfully' };
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to restore medicine';
      return rejectWithValue({ message: errorMessage });
    }
  }
);

const medicinesSlice = createSlice({
  name: 'medicines',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMedicines.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
    .addCase(fetchMedicines.fulfilled, (state, action) => {
      state.loading = false;
      console.log('=== FETCH MEDICINES CALLED ===');
      console.log('Medicines API response:', action.payload);
      console.log('Current state medicines count:', state.medicines.length);
        // Handle different possible response structures
        let medicines = [];
        if (Array.isArray(action.payload.data)) {
          // Handle Strapi format: data[].attributes
          medicines = action.payload.data.map((item: any) => ({
            medicine_id: item.id,
            medicine_name: item.attributes?.medicine_name || item.medicine_name,
            generic_name: item.attributes?.generic_name || item.generic_name,
            manufacturer: item.attributes?.manufacturer || item.manufacturer || '',
            description: item.attributes?.description || item.description || '',
            is_active: item.attributes?.is_active !== undefined ? item.attributes.is_active : item.is_active,
            created_at: item.attributes?.createdAt || item.created_at,
            updated_at: item.attributes?.updatedAt || item.updated_at,
            // Handle category data - check if category.data exists and is not null
            category_id: item.attributes?.category?.data?.id || item.category_id || null,
            category_name: (item.attributes?.category?.data && item.attributes.category.data.id) 
              ? item.attributes.category.data.attributes?.category_name 
              : (item.category_name || 'No Category'),
            // Add default values for fields that might not exist in Strapi
            expiry_date: item.attributes?.expiry_date || item.expiry_date || new Date().toISOString().split('T')[0],
            unit_price: item.attributes?.unit_price || item.unit_price || 0,
            quantity: item.attributes?.quantity || item.quantity || 0,
          }));
        } else if (Array.isArray(action.payload)) {
          medicines = action.payload;
        } else if (action.payload && Array.isArray(action.payload.medicines)) {
          medicines = action.payload.medicines;
        }
        console.log('Processed medicines:', medicines);
        console.log('Sample medicine category data:', medicines[0] ? {
          category_id: medicines[0].category_id,
          category_name: medicines[0].category_name
        } : 'No medicines');
        
        // Debug category extraction for each medicine
        medicines.forEach((medicine: any, index: number) => {
          console.log(`Medicine ${index + 1} category extraction:`, {
            raw_category: action.payload.data[index]?.attributes?.category,
            extracted_category_id: medicine.category_id,
            extracted_category_name: medicine.category_name
          });
        });
        state.medicines = medicines;
        state.pagination = action.payload.pagination || action.payload.meta?.pagination || {
          current: 1,
          pageSize: 10,
          total: 0,
          totalPages: 0,
        };
      })
      .addCase(fetchMedicines.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch medicines';
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        console.log('Categories API response:', action.payload);
        // Handle different possible response structures
        let categories = [];
        if (Array.isArray(action.payload.data)) {
          // Handle Strapi format: data[].attributes
          categories = action.payload.data.map((item: any) => ({
            category_id: item.id,
            category_name: item.attributes?.category_name || item.category_name,
            description: item.attributes?.description || item.description
          }));
        } else if (Array.isArray(action.payload)) {
          categories = action.payload;
        } else if (action.payload && Array.isArray(action.payload.categories)) {
          categories = action.payload.categories;
        }
        console.log('Processed categories:', categories);
        state.categories = categories;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        console.error('Failed to fetch categories:', action.error);
        state.categories = [];
      })
      .addCase(createMedicine.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMedicine.fulfilled, (state, action) => {
        state.loading = false;
        console.log('=== CREATE MEDICINE FULFILLED ===');
        console.log('Create medicine response:', action.payload);
        // Handle Strapi format for created medicine
        let newMedicine = action.payload.data || action.payload;
        if (newMedicine && newMedicine.attributes) {
          newMedicine = {
            medicine_id: newMedicine.id,
            medicine_name: newMedicine.attributes.medicine_name,
            generic_name: newMedicine.attributes.generic_name,
            manufacturer: newMedicine.attributes.manufacturer || '',
            description: newMedicine.attributes.description || '',
            is_active: newMedicine.attributes.is_active,
            created_at: newMedicine.attributes.createdAt,
            updated_at: newMedicine.attributes.updatedAt,
            // Handle category data - check if category.data exists and is not null
            category_id: newMedicine.attributes.category?.data?.id || null,
            category_name: (newMedicine.attributes.category?.data && newMedicine.attributes.category.data.id) 
              ? newMedicine.attributes.category.data.attributes?.category_name 
              : 'No Category',
            expiry_date: newMedicine.attributes.expiry_date || new Date().toISOString().split('T')[0],
            unit_price: newMedicine.attributes.unit_price || 0,
            quantity: newMedicine.attributes.quantity || 0,
          };
          console.log('Mapped new medicine category:', {
            category_id: newMedicine.category_id,
            category_name: newMedicine.category_name,
            raw_category: action.payload.data?.attributes?.category
          });
        }
        console.log('Processed new medicine:', newMedicine);
        state.medicines.push(newMedicine);
      })
      .addCase(createMedicine.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create medicine';
      })
      .addCase(updateMedicine.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMedicine.fulfilled, (state, action) => {
        state.loading = false;
        // The API returns { success: true, data: medicine, message: "..." }
        const updatedMedicine = action.payload.data || action.payload;
        const index = state.medicines.findIndex(medicine => medicine.medicine_id === updatedMedicine.medicine_id);
        if (index !== -1) {
          // Replace the medicine with the updated data from API
          state.medicines[index] = updatedMedicine;
        }
      })
      .addCase(updateMedicine.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update medicine';
      })
      .addCase(deleteMedicine.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMedicine.fulfilled, (state, action) => {
        state.loading = false;
        state.medicines = state.medicines.filter(medicine => medicine.medicine_id !== action.payload.id);
        state.pagination.total -= 1;
      })
      .addCase(deleteMedicine.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete medicine';
      })
      .addCase(createCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.categories.push(action.payload);
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create category';
      })
      .addCase(updateCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.categories.findIndex(category => category.category_id === action.payload.category_id);
        if (index !== -1) {
          state.categories[index] = action.payload;
        }
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update category';
      })
      .addCase(deleteCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = state.categories.filter(category => category.category_id !== action.payload.id);
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete category';
      })
      .addCase(fetchDeletedMedicines.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeletedMedicines.fulfilled, (state, action) => {
        state.loading = false;
        console.log('Deleted medicines API response:', action.payload);
        // Handle Strapi format for deleted medicines too
        let deletedMedicines = [];
        if (Array.isArray(action.payload.data)) {
          deletedMedicines = action.payload.data.map((item: any) => ({
            medicine_id: item.id,
            medicine_name: item.attributes?.medicine_name || item.medicine_name,
            generic_name: item.attributes?.generic_name || item.generic_name,
            manufacturer: item.attributes?.manufacturer || item.manufacturer || '',
            description: item.attributes?.description || item.description || '',
            is_active: item.attributes?.is_active !== undefined ? item.attributes.is_active : item.is_active,
            created_at: item.attributes?.createdAt || item.created_at,
            updated_at: item.attributes?.updatedAt || item.updated_at,
            deleted_at: item.attributes?.deletedAt || item.deleted_at,
            // Handle category data - check if category.data exists and is not null
            category_id: item.attributes?.category?.data?.id || item.category_id || null,
            category_name: (item.attributes?.category?.data && item.attributes.category.data.id) 
              ? item.attributes.category.data.attributes?.category_name 
              : (item.category_name || 'No Category'),
            // Add default values for fields that might not exist in Strapi
            expiry_date: item.attributes?.expiry_date || item.expiry_date || new Date().toISOString().split('T')[0],
            unit_price: item.attributes?.unit_price || item.unit_price || 0,
            quantity: item.attributes?.quantity || item.quantity || 0,
          }));
        } else if (Array.isArray(action.payload)) {
          deletedMedicines = action.payload;
        }
        console.log('Processed deleted medicines:', deletedMedicines);
        state.deletedMedicines = deletedMedicines;
      })
      .addCase(fetchDeletedMedicines.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch deleted medicines';
      })
      .addCase(restoreMedicine.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreMedicine.fulfilled, (state, action) => {
        state.loading = false;
        state.deletedMedicines = state.deletedMedicines.filter(medicine => medicine.medicine_id !== action.payload.id);
      })
      .addCase(restoreMedicine.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to restore medicine';
      });
  },
});

export default medicinesSlice.reducer;
